import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

// Categorical slots (validated palette) — fixed order, never cycled.
const PAYER_META = [
  ["commercial", "Commercial", "#2a78d6"],
  ["medicare", "Medicare", "#eb6834"],
  ["medicaid", "Medicaid", "#1baf7a"],
  ["workers_comp", "Workers comp", "#eda100"],
  ["self_pay", "Self-pay", "#e87ba4"],
  ["other", "Other", "#008300"],
];

// Ordinal appropriateness — one hue, light→dark.
const APPR_META = [
  ["0", "0 · Inappropriate", "#cde2fb"],
  ["1", "1 · Reasonable", "#86b6ef"],
  ["2", "2 · Ideal", "#3987e5"],
];

const baseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 10 } } },
    y: { beginAtZero: true, ticks: { precision: 0, font: { size: 10 } }, grid: { color: "rgba(0,0,0,0.06)" } },
  },
};

function Card({ title, subtitle, children }) {
  return (
    <div className="p-3 bg-white border border-gray-200 rounded-lg">
      <div className="text-xs font-semibold text-gray-700">{title}</div>
      {subtitle && <div className="text-[11px] text-gray-400 mb-1">{subtitle}</div>}
      {children}
    </div>
  );
}

/**
 * Dashboard distributions from logged referral events:
 * payer mix, surgical conversion, appropriateness. Direct-labeled bars.
 */
export default function ReferralCharts({ summary }) {
  if (!summary || !summary.total_events) return null;

  const payerRows = PAYER_META.filter(([key]) => summary.insurance?.[key]);
  const payerData = {
    labels: payerRows.map(([, label]) => label),
    datasets: [
      {
        data: payerRows.map(([key]) => summary.insurance[key]),
        backgroundColor: payerRows.map(([, , color]) => color),
        borderRadius: 4,
        maxBarThickness: 26,
      },
    ],
  };

  const apprRows = APPR_META.map(([key, label, color]) => [label, summary.appropriateness?.[key] || 0, color]);
  const apprData = {
    labels: apprRows.map(([label]) => label),
    datasets: [
      {
        data: apprRows.map(([, n]) => n),
        backgroundColor: apprRows.map(([, , color]) => color),
        borderRadius: 4,
        maxBarThickness: 26,
      },
    ],
  };

  const surgYes = summary.surgical?.yes || 0;
  const surgNo = summary.surgical?.no || 0;
  const surgTotal = surgYes + surgNo;
  const surgPct = surgTotal ? Math.round((100 * surgYes) / surgTotal) : null;

  const insuranceTotal = payerRows.reduce((sum, [key]) => sum + summary.insurance[key], 0);
  const commercialPct = insuranceTotal
    ? Math.round((100 * (summary.insurance?.commercial || 0)) / insuranceTotal)
    : null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
      <Card title="Payer Distribution" subtitle={commercialPct != null ? `${commercialPct}% commercial of ${insuranceTotal} with insurance` : "no insurance logged yet"}>
        <div className="h-36">
          <Bar
            data={payerData}
            options={{
              ...baseOptions,
              indexAxis: "y",
              scales: {
                x: { beginAtZero: true, ticks: { precision: 0, font: { size: 10 } }, grid: { color: "rgba(0,0,0,0.06)" } },
                y: { grid: { display: false }, ticks: { font: { size: 10 } } },
              },
            }}
          />
        </div>
      </Card>

      <Card title="Surgical Conversion" subtitle={`${surgTotal} referrals with outcome`}>
        {surgPct == null ? (
          <div className="text-sm text-gray-400 py-6 text-center">no outcomes logged yet</div>
        ) : (
          <div className="py-3">
            <div className="text-3xl font-bold text-primary">{surgPct}%</div>
            <div className="text-[11px] text-gray-500 mb-2">
              {surgYes} surgical · {surgNo} non-surgical
            </div>
            <div className="flex h-3 rounded-full overflow-hidden border border-gray-200" title={`${surgYes} of ${surgTotal} to surgery`}>
              <div style={{ width: `${surgPct}%`, background: "#2a78d6" }} />
              <div className="flex-1 bg-gray-200" />
            </div>
          </div>
        )}
      </Card>

      <Card title="Appropriateness" subtitle={`0–2 rating across ${Object.values(summary.appropriateness || {}).reduce((a, b) => a + b, 0)} rated referrals`}>
        <div className="h-36">
          <Bar data={apprData} options={baseOptions} />
        </div>
      </Card>
    </div>
  );
}
